import {
  Component, inject, input, output, signal, OnInit, OnDestroy,
  ViewEncapsulation, ElementRef, viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { PaymentService } from '../../../../core/services/payment.service';
import { AppStateService } from '../../../../core/services/app-state.service';
import { loadScript } from '../../../../core/utils/script-loader';

declare const braintree: any;

@Component({
  selector: 'app-braintree-gateway',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, TranslateModule],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="braintree-gateway">
      @if (loading()) {
        <div class="gateway-loading">
          <mat-spinner diameter="36"></mat-spinner>
          <p>{{ 'PAYMENT_PROCESSING.BRAINTREE.LOADING' | translate }}</p>
        </div>
      }

      <div #braintreeContainer class="braintree-dropin-container"></div>

      @if (ready()) {
        <button mat-flat-button color="primary" class="gateway-submit-btn"
                [disabled]="submitting()"
                (click)="submit()">
          @if (submitting()) {
            <mat-spinner diameter="20"></mat-spinner>
            {{ 'PAYMENT_PROCESSING.BRAINTREE.PROCESSING' | translate }}
          } @else {
            {{ 'PAYMENT_PROCESSING.BRAINTREE.SUBMIT' | translate }}
          }
        </button>
      }
    </div>
  `,
  styles: [`
    .braintree-gateway {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .gateway-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px;
      color: #666;
    }
    .braintree-dropin-container {
      min-height: 200px;
    }
    .gateway-submit-btn {
      width: 100%;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
  `]
})
export class BraintreeGatewayComponent implements OnInit, OnDestroy {
  orderId = input.required<number>();
  gatewayData = input<Record<string, any>>();
  // 3D Secure params passed to requestPaymentMethod - at least { amount }, plus
  // optional email/billingAddress to improve frictionless verification. The
  // backend requires verified 3DS info on the nonce, so this must be provided.
  threeDSecure = input<Record<string, any>>({});

  completed = output<{ orderId: number; status: string }>();
  // Carries the HTTP status when known so the host can react - e.g. a 400
  // (non-payable, often already-paid) should trigger an order reload.
  failed = output<{ message: string; status?: number }>();

  private paymentService = inject(PaymentService);
  private appState = inject(AppStateService);
  private translate = inject(TranslateService);
  private braintreeContainer = viewChild<ElementRef>('braintreeContainer');

  loading = signal(true);
  ready = signal(false);
  submitting = signal(false);
  private authorization: string | null = null;
  private dropinInstance: any = null;
  private threeDSecureModalRendered = false;
  private pendingThreeDSecureLogTimeout: number | null = null;
  private blankThreeDSecureIframeTimeout: number | null = null;
  private submitAttemptId = 0;
  private activeSubmitAttemptId: number | null = null;
  private expiredSubmitAttemptIds = new Set<number>();

  async ngOnInit(): Promise<void> {
    try {
      const config = await firstValueFrom(this.paymentService.getBraintreeConfig(this.orderId()));
      this.authorization = config.authorization;
      await loadScript('https://js.braintreegateway.com/web/dropin/1.43.0/js/dropin.min.js', 'braintree-dropin-sdk');
      await this.createDropinInstance();
    } catch (err: any) {
      console.error('[BraintreeGateway] Init error:', err);
      this.loading.set(false);
      this.ready.set(false);
      this.failed.emit({
        message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC'),
        status: Number(err?.status) || undefined
      });
    }
  }

  async submit(): Promise<void> {
    if (!this.dropinInstance || this.submitting()) return;

    const attemptId = ++this.submitAttemptId;
    this.activeSubmitAttemptId = attemptId;
    this.submitting.set(true);
    const requestThreeDSecure = this.buildRequestThreeDSecureOptions();

    try {
      if (!requestThreeDSecure['amount']) {
        console.error('[BraintreeGateway] Missing 3DS amount before requestPaymentMethod.', {
          orderId: this.orderId(),
          threeDSecure: requestThreeDSecure
        });
        this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC') });
        return;
      }

      console.info('[BraintreeGateway] Starting requestPaymentMethod with 3DS.', {
        orderId: this.orderId(),
        attemptId,
        dropinVersion: this.getDropinVersion(),
        browser: this.getBrowserContext(),
        createThreeDSecure: this.buildCreateThreeDSecureOptions(),
        requestThreeDSecure
      });

      this.startThreeDSecureDiagnostics(requestThreeDSecure, attemptId);

      // Passing the 3DS params triggers the verification (frictionless or
      // challenge) and attaches the result to the returned payload/nonce.
      const payload = await this.dropinInstance.requestPaymentMethod({
        threeDSecure: requestThreeDSecure
      });

      if (this.activeSubmitAttemptId !== attemptId || this.expiredSubmitAttemptIds.has(attemptId)) {
        this.expiredSubmitAttemptIds.delete(attemptId);
        console.warn('[BraintreeGateway] Ignoring late requestPaymentMethod resolution from an expired 3DS attempt.', {
          orderId: this.orderId(),
          attemptId,
          payload
        });
        return;
      }

      this.stopThreeDSecureDiagnostics();

      console.info('[BraintreeGateway] requestPaymentMethod resolved.', {
        orderId: this.orderId(),
        attemptId,
        type: payload?.type,
        liabilityShifted: payload?.liabilityShifted,
        liabilityShiftPossible: payload?.liabilityShiftPossible,
        threeDSecureInfo: payload?.threeDSecureInfo
      });

      // If this is a card and liability did NOT shift even though it was
      // possible (challenge failed/abandoned), do not submit - the backend
      // needs verified 3DS info. Reset the Drop-in so the buyer can retry.
      if (payload.type === 'CreditCard' && !payload.liabilityShifted && payload.liabilityShiftPossible) {
        this.dropinInstance?.clearSelectedPaymentMethod?.();
        this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC') });
        return;
      }

      const result = await firstValueFrom(this.paymentService.executeBraintree(this.orderId(), payload.nonce));

      if (result.success) {
        // Sale submitted + order confirmed (covers confirmed/pending) - emit a
        // neutral status; the order-details view reloads to show the real state.
        this.completed.emit({ orderId: this.orderId(), status: 'payment_pending' });
      }
    } catch (err: any) {
      if (this.activeSubmitAttemptId !== attemptId || this.expiredSubmitAttemptIds.has(attemptId)) {
        this.expiredSubmitAttemptIds.delete(attemptId);
        console.warn('[BraintreeGateway] Ignoring late requestPaymentMethod rejection from an expired 3DS attempt.', {
          orderId: this.orderId(),
          attemptId,
          error: err
        });
        return;
      }

      this.stopThreeDSecureDiagnostics();
      console.error('[BraintreeGateway] Submit error:', {
        error: err,
        orderId: this.orderId(),
        attemptId,
        threeDSecure: requestThreeDSecure,
        cardinal: this.getCardinalModalSnapshot()
      });

      const status = Number(err?.status) || undefined;
      if (status === 400) {
        // Order no longer payable (often already paid) - host reloads and confirms.
        this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC'), status });
      } else {
        // Declined / transaction failed (422). The nonce is single-use - reset
        // the Drop-in so the buyer can pick another method, then surface the error.
        this.dropinInstance?.clearSelectedPaymentMethod?.();
        this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC') });
      }
    } finally {
      if (this.activeSubmitAttemptId === attemptId) {
        this.activeSubmitAttemptId = null;
        this.submitting.set(false);
      } else if (this.activeSubmitAttemptId === null && this.expiredSubmitAttemptIds.has(attemptId)) {
        this.expiredSubmitAttemptIds.delete(attemptId);
        this.submitting.set(false);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopThreeDSecureDiagnostics();
    this.activeSubmitAttemptId = null;
    this.expiredSubmitAttemptIds.clear();
    if (this.dropinInstance) {
      this.dropinInstance.teardown().catch(() => {});
      this.dropinInstance = null;
    }
  }

  private async createDropinInstance(): Promise<void> {
    const container = this.braintreeContainer()?.nativeElement;
    if (!container) {
      throw new Error('Container not available');
    }

    if (!this.authorization) {
      throw new Error('Braintree authorization not available');
    }

    const createThreeDSecure = this.buildCreateThreeDSecureOptions();

    if (!createThreeDSecure['amount']) {
      throw new Error('3D Secure amount is required before initializing Braintree Drop-in.');
    }

    container.innerHTML = '';
    this.loading.set(true);
    this.ready.set(false);

    this.dropinInstance = await braintree.dropin.create({
      authorization: this.authorization,
      container,
      locale: this.getBraintreeLocale(),
      card: { cardholderName: { required: true } },
      // Drop-in 1.43.0 accepts a full threeDSecure create object. The verify
      // fields still belong on requestPaymentMethod, but cardinalSDKConfig must
      // be provided here and the create-time amount keeps the configuration in
      // sync with the later verifyCard call.
      threeDSecure: createThreeDSecure
    });

    this.registerThreeDSecureEventLogging();

    console.info('[BraintreeGateway] Drop-in initialized.', {
      orderId: this.orderId(),
      dropinVersion: this.getDropinVersion(),
      browser: this.getBrowserContext(),
      threeDSecureCreate: createThreeDSecure
    });

    this.loading.set(false);
    this.ready.set(true);
  }

  /** Express and Braintree both use canonical underscore locale identifiers. */
  private getBraintreeLocale(): string {
    const locale = this.appState.getLocale();
    const supported = new Set([
      'pt_PT', 'pt_BR', 'es_ES', 'en_US', 'fr_FR', 'de_DE', 'it_IT', 'nl_NL'
    ]);
    return supported.has(locale) ? locale : 'en_US';
  }

  private buildCreateThreeDSecureOptions(): Record<string, any> {
    const requestThreeDSecure = this.buildRequestThreeDSecureOptions();

    return {
      amount: requestThreeDSecure['amount'],
      cardinalSDKConfig: {
        timeout: 60000,
        maxRequestRetries: 2,
        payment: {
          displayLoading: true,
          displayExitButton: true
        }
      }
    };
  }

  private buildRequestThreeDSecureOptions(): Record<string, any> {
    const requestThreeDSecure = { ...(this.threeDSecure() || {}) };

    if (requestThreeDSecure['amount'] != null) {
      requestThreeDSecure['amount'] = String(requestThreeDSecure['amount']);
    }

    // Braintree recommends sending as much customer/device data as possible to
    // improve 3DS v2 outcomes and reduce unnecessary challenges.
    requestThreeDSecure['collectDeviceData'] = true;

    return requestThreeDSecure;
  }

  private registerThreeDSecureEventLogging(): void {
    if (!this.dropinInstance?.on) return;

    this.dropinInstance.on('3ds:authentication-modal-render', () => {
      this.threeDSecureModalRendered = true;
      console.info('[BraintreeGateway] 3DS challenge modal render event.', this.getCardinalModalSnapshot());
      this.scheduleBlankThreeDSecureIframeCheck();

      window.setTimeout(() => {
        if (!this.submitting()) return;
        console.info('[BraintreeGateway] 3DS challenge modal post-render snapshot.', this.getCardinalModalSnapshot());
      }, 250);
    });

    this.dropinInstance.on('3ds:authentication-modal-close', () => {
      console.info('[BraintreeGateway] 3DS challenge modal close event.', this.getCardinalModalSnapshot());
    });

    this.dropinInstance.on('3ds:customer-canceled', () => {
      console.warn('[BraintreeGateway] 3DS challenge canceled by customer.', this.getCardinalModalSnapshot());
    });
  }

  private startThreeDSecureDiagnostics(threeDSecure: Record<string, any>, attemptId: number): void {
    this.stopThreeDSecureDiagnostics();
    this.threeDSecureModalRendered = false;

    this.pendingThreeDSecureLogTimeout = window.setTimeout(() => {
      if (!this.submitting() || this.threeDSecureModalRendered || this.activeSubmitAttemptId !== attemptId) return;

      console.error('[BraintreeGateway] 3DS verification is still pending before the challenge modal rendered.', {
        orderId: this.orderId(),
        attemptId,
        threeDSecure,
        cardinal: this.getCardinalModalSnapshot()
      });
    }, 10000);
  }

  private scheduleBlankThreeDSecureIframeCheck(): void {
    if (this.blankThreeDSecureIframeTimeout !== null) {
      window.clearTimeout(this.blankThreeDSecureIframeTimeout);
    }

    const attemptId = this.activeSubmitAttemptId;
    if (attemptId === null) return;

    this.blankThreeDSecureIframeTimeout = window.setTimeout(() => {
      if (!this.submitting() || this.activeSubmitAttemptId !== attemptId) return;

      const cardinalSnapshot = this.getCardinalModalSnapshot();

      if (!this.isBlankThreeDSecureIframe(cardinalSnapshot)) return;

      console.error('[BraintreeGateway] 3DS modal rendered but the Cardinal challenge iframe stayed blank.', {
        orderId: this.orderId(),
        attemptId,
        browser: this.getBrowserContext(),
        cardinal: cardinalSnapshot
      });

      void this.resetDropinAfterHungThreeDSecure(this.buildHungThreeDSecureMessage());
    }, 8000);
  }

  private stopThreeDSecureDiagnostics(): void {
    if (this.pendingThreeDSecureLogTimeout !== null) {
      window.clearTimeout(this.pendingThreeDSecureLogTimeout);
      this.pendingThreeDSecureLogTimeout = null;
    }

    if (this.blankThreeDSecureIframeTimeout !== null) {
      window.clearTimeout(this.blankThreeDSecureIframeTimeout);
      this.blankThreeDSecureIframeTimeout = null;
    }
  }

  private async resetDropinAfterHungThreeDSecure(message: string): Promise<void> {
    const attemptId = this.activeSubmitAttemptId;
    if (attemptId !== null) {
      this.expiredSubmitAttemptIds.add(attemptId);
    }

    this.activeSubmitAttemptId = null;
    this.submitting.set(false);
    this.stopThreeDSecureDiagnostics();
    this.ready.set(false);
    this.loading.set(true);

    try {
      await this.dropinInstance?.teardown?.();
    } catch (err) {
      console.error('[BraintreeGateway] Error while tearing down Drop-in after a hung 3DS challenge.', err);
    } finally {
      this.dropinInstance = null;
    }

    try {
      await this.createDropinInstance();
    } catch (err) {
      console.error('[BraintreeGateway] Failed to recreate Drop-in after a hung 3DS challenge.', {
        orderId: this.orderId(),
        attemptId,
        error: err
      });
      this.loading.set(false);
      this.ready.set(false);
    }

    this.failed.emit({ message });
  }

  private isBlankThreeDSecureIframe(cardinalSnapshot: Record<string, any>): boolean {
    return Boolean(
      cardinalSnapshot['iframePresent'] &&
      cardinalSnapshot['iframeAccessible'] &&
      !cardinalSnapshot['iframeSrcAttribute'] &&
      cardinalSnapshot['iframeReadyState'] === 'complete' &&
      cardinalSnapshot['iframeBodyChildElementCount'] === 1 &&
      !cardinalSnapshot['iframeBodyTextContent']
    );
  }

  private buildHungThreeDSecureMessage(): string {
    if (this.getBrowserContext()['isFirefox']) {
      return this.translate.instant('UI_TEXT.BRAINTREE_3DS_FIREFOX');
    }

    return this.translate.instant('UI_TEXT.BRAINTREE_3DS_RETRY');
  }

  private getDropinVersion(): string | null {
    return braintree?.dropin?.VERSION || null;
  }

  private getBrowserContext(): Record<string, any> {
    const userAgent = navigator.userAgent || '';

    return {
      isFirefox: /firefox/i.test(userAgent),
      language: navigator.language || null,
      userAgent
    };
  }

  private getCardinalModalSnapshot(): Record<string, any> {
    const container = document.getElementById('Cardinal-ElementContainer');
    const modal = document.getElementById('Cardinal-Modal') as HTMLElement | null;
    const modalContent = document.getElementById('Cardinal-ModalContent') as HTMLElement | null;
    const iframe = document.getElementById('Cardinal-CCA-IFrame') as HTMLIFrameElement | null;
    const iframeTargetForm = document.querySelector('form[target="Cardinal-CCA-IFrame"]') as HTMLFormElement | null;

    let iframeLocationHref: string | null = null;
    let iframeReadyState: string | null = null;
    let iframeAccessible = false;
    let iframeBodyChildElementCount: number | null = null;
    let iframeBodyTextContent: string | null = null;

    try {
      iframeAccessible = !!iframe?.contentDocument;
      iframeLocationHref = iframe?.contentWindow?.location?.href || null;
      iframeReadyState = iframe?.contentDocument?.readyState || null;
      iframeBodyChildElementCount = iframe?.contentDocument?.body?.childElementCount ?? null;
      iframeBodyTextContent = iframe?.contentDocument?.body?.textContent?.trim() || null;
    } catch {
      iframeLocationHref = null;
    }

    return {
      containerPresent: !!container,
      modalPresent: !!modal,
      modalDisplay: modal ? getComputedStyle(modal).display : null,
      modalPosition: modal ? getComputedStyle(modal).position : null,
      modalVisibility: modal ? getComputedStyle(modal).visibility : null,
      modalZIndex: modal ? getComputedStyle(modal).zIndex : null,
      modalRect: modal ? modal.getBoundingClientRect().toJSON() : null,
      modalContentClassName: modalContent?.className || null,
      iframePresent: !!iframe,
      iframeSrcAttribute: iframe?.getAttribute('src') || null,
      iframeAccessible,
      iframeLocationHref,
      iframeReadyState,
      iframeBodyChildElementCount,
      iframeBodyTextContent,
      iframeTargetFormPresent: !!iframeTargetForm,
      iframeTargetFormAction: iframeTargetForm?.action || null,
      iframeTargetFormMethod: iframeTargetForm?.method || null
    };
  }
}
